/*eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { get_api, post_api } from "api";
import {
        getUserChatList,
        listenForUserChatListUpdates,
        deleteUserFromUserChatList,
        getChatListItemByChatId,
        listenForNewMessages,
        addChatsRecord,
        getMessagesForChat,
        addMessageToChat,
        moveChatToArchive,
        storage
} from "firebaseConfig";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { useAuth } from "context/AuthContext";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import imageCompression from "browser-image-compression";

dayjs.extend(utc);
dayjs.extend(timezone);

const useMessagesHook = () => {
    const { companyProfile, userData } = useAuth();
    const location = useLocation();

    const [locumUserData, setLocumUserData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [receiverObj, setReceiverObj] = useState({});
    const [receiverLoader, setReceiverLoader] = useState(false);
    const [allMessages, setAllMessages] = useState([]);
    const [text, setText] = useState("");
    const [chatUsersList, setChatUsersList] = useState([]);
    const [archiveCount, setArchiveCount] = useState(0);
    const [archiveView, setArchiveView] = useState(false);

    const getAllChatUsersList = async (isArchived) => {
        try {
            isArchived = isArchived ?? archiveView;
            setIsLoading(true);
            const chat_id = userData.id ?? userData._id ?? companyProfile.userId;
            const { chatList, archiveRecordCount } = await getUserChatList(
                chat_id,
                isArchived
            );
            console.log("🚀 ~ getAllChatUsersList ~ chatList:", chatList, chat_id)
            setChatUsersList(chatList);
            setArchiveCount(archiveRecordCount);
            setIsLoading(false);
        } catch (err) {
            console.log("🚀 ~ getAllChatUsersList ~ err:", err);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getAllChatUsersList();
        try{
            let unsubscribe;
                
            const startListening = async () => {
                unsubscribe = listenForUserChatListUpdates(
                   userData.id,
                   ({ chatList, archivedCount }) => {
                       console.log("🚀 ~ listenForUserChatListUpdates ~ chatList:", chatList)
                       setChatUsersList(chatList);
                       setArchiveCount(archivedCount);
                   },
                   archiveView
                );
            };
            return () => {
                // Check if unsubscribe is a function before calling
                if (typeof unsubscribe === 'function') {
                    unsubscribe();
                }
            };
        } catch (error) {
            console.error("Failed to register for chat updates:", error);
        }
    }, []);

    const getLocumUserById = async (id) => {
        try {
            const res = await get_api(`profile/${id}/locum`);
            setLocumUserData(res?.data?.locumUsersProfile);
            return true;
        } catch (err) {
            toast.error(err.message);
        }
    };

    const showMessagesDirectly = async () => {
        try{
            const recData = await getChatListItemByChatId(
                companyProfile.userId,
                location?.state?.chatId
            );
            await handleAddChatsRecord(location?.state?.chatId, recData);
        } catch (error) {
            console.error("Error with getChatListItemByChatId in showMessagesDirectly():", error);
        }
    };

    useEffect(() => {
        if (location?.state?.chatId) {
            showMessagesDirectly();
        }
    }, [location?.state?.chatId]);

    useEffect(() => {
        getLocumUserById(receiverObj?.chat_user_id);
    }, [receiverObj?.chat_user_id]);

    const handleNewMessages = async (newMessages) => {
        setAllMessages(newMessages);
        console.log("🚀 ~ handleNewMessages ~ companyProfile:", companyProfile);
        const chat_id = userData.id ?? userData._id ?? companyProfile.userId;
        if( null === chat_id ){
            return; // Can't listed for messages unless we have a valid chat id
        }
        try{
            const { chatList, archiveRecordCount } = await getUserChatList(
                chat_id,
                archiveView
            );
            console.log("🚀 ~ handleNewMessages ~ chatList:", chatList)
            setChatUsersList(chatList);
            setArchiveCount(archiveRecordCount);
        } catch (error) {
            console.error("Error fetching new messages:", error);
        }
    };

    useEffect(() => {
        if( typeof receiverObj?.chat_id == "undefined" || null === receiverObj?.chat_id ){
            return;
        }
        try{
            let unsubscribe;
            // Start listening for new messages
            const startListening = async () => {
                // Await the listener setup to get the actual unsubscribe function
                unsubscribe = await listenForNewMessages(
                     receiverObj.chat_id,
                     handleNewMessages
                 );
            };
            
            startListening();

            // Clean up the listener when the component unmounts
            return () => {
                if (typeof unsubscribe === 'function') {
                    unsubscribe();
                }
            };
        } catch (error) {
           console.error("Error with registering for new messages:", error);
        }
    }, [receiverObj?.chat_id]);

    const handleAddChatsRecord = async (chatId, chatObj) => {
        setReceiverLoader(true);
        try{
            await addChatsRecord(chatId, companyProfile.userId);
            setReceiverObj(chatObj);
            const getAllMsgs = await getMessagesForChat(chatId);
            setAllMessages(getAllMsgs);
        } catch (error) {
            console.error("Error with handing add chats record():", error);
        }
        setReceiverLoader(false);
    };

    const handleUpload = async (file) => {
        // Compress the image
        const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
        };
        const compressedFile = await imageCompression(file, options);

        // Add a timestamp to the file name
        const timestamp = Date.now();
        const fileName = `${timestamp}_${file.name}`;

        try{
            // Create a reference to the file in Firebase Storage
            const storageRef = ref(storage, `uploads/${fileName}`);
            // const storageRef = ref(storage, `uploads/${file.name}`);
            const uploadTask = uploadBytesResumable(storageRef, compressedFile);

            uploadTask.on(
                "state_changed",
                (snapshot) => {
                    const progress =
                        (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    console.log("Upload is " + progress + "% done");
                },
                (error) => {
                    console.error(error);
                    console.log("Upload failed");
                },
                () => {
                    // Handle successful uploads
                    getDownloadURL(uploadTask.snapshot.ref).then(async (downloadURL) => {
                        await handleSendMessage(null, downloadURL);
                    });
                }
            );
        } catch (error) {
            console.error("Failed to upload file:", error);
            toast.error("Failed to upload file to chat. See browser console for errors.");
        }

        // Prevent default upload behavior by returning false
        return false;
    };

    const handleSendMessage = async (_e, image_url = "") => {
        if (!text.trim() && !image_url.trim()) return;
        const msgObj = {
            image_url,
            text,
            profile_photo: receiverObj?.chat_user_profile_picture,
            receiver_id: receiverObj?.chat_user_id,
            receiver_name: receiverObj?.chat_user_name,
            sender_id: companyProfile?.userId,
            read: false,
            time: new Date(),
        };
        try{
            await addMessageToChat(receiverObj.chat_id, msgObj);
        } catch (error) {
            console.error("Error sending message:", error);
        }
        
        const msgData = {
            chat_id: receiverObj.chat_id,
            text: text,
            image_url: "",
            sender_id: companyProfile?.userId,
            receiver_id: receiverObj?.chat_user_id,
            profile_photo: receiverObj?.chat_user_profile_picture,
        };
        await post_api(`chat/send-notification`, { locumUserId: receiverObj?.chat_user_id, chat_id: receiverObj?.chat_id, msgData: msgData });
        setText("");
        try{
            const getAllMsgs = await getMessagesForChat(receiverObj.chat_id);
            setAllMessages(getAllMsgs);
        } catch (error) {
            console.error("Error with fetching new messages after send:", error);
        }
    };

    const formatMessageTime = (timestamp) => {
        const milliseconds =
            (timestamp.seconds || 0) * 1000 + (timestamp.nanoseconds || 0) / 1000000;
        const date = dayjs(milliseconds);

        // Check if the date is valid
        if (!date.isValid()) {
            return "Invalid Time";
        }

        // Format the date to show only the time
        return date.tz("Asia/Kolkata").format("h:mm A"); // Adjust timezone if needed
    };

    const handleDeleteChat = async (recData) => {
        if (companyProfile?.userId && recData?.chat_id) {
            setIsLoading(true);
            try{
                await deleteUserFromUserChatList(companyProfile.userId, recData.chat_id);
                const { chatList, archiveRecordCount } = await getUserChatList(
                    companyProfile.userId,
                    archiveView
                );
                console.log("🚀 ~ handleDeleteChat ~ chatList:", chatList)
                setChatUsersList(chatList);
                setArchiveCount(archiveRecordCount);
                setReceiverObj({});
            } catch (error) {
                console.error("Error deleting chat:", error);
                toast.error("Failed to delete chat. See browser console for errors.");
            }
            
            setIsLoading(false);
        }
    };

    const handleArchive = async (chatId) => {
        try{
            await moveChatToArchive(
                companyProfile.userId,
                chatId,
                !archiveView ? true : false
            );
            setArchiveView(false);
        } catch (error) {
            console.error("Failed to archive chat:", error);
            toast.error("Failed to archive chat. See browser console for errors.");
        }
    };

    const onSearch = (value, _e, info) => {
        console.log("🚀 ~ onSearch ~ value, _e, info:", value, _e, info);
    };

    return {
        chatUsersList,
        isLoading,
        locumUserData,
        open,
        setOpen,
        handleSendMessage,
        formatMessageTime,
        handleDeleteChat,
        receiverObj,
        receiverLoader,
        allMessages,
        text,
        setText,
        companyProfile,
        handleAddChatsRecord,
        handleArchive,
        archiveCount,
        archiveView,
        setArchiveView,
        setChatUsersList,
        // getAllArchiveUsersList,
        getAllChatUsersList,
        onSearch,
        handleUpload,
    };
};

export default useMessagesHook;
